// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { toErr } from '@easyflow/shared';
import {
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { FileText, Search, Plus, Clock, DollarSign, TrendingUp, Download, Archive } from 'lucide-react';
import api from '@/lib/api';
import { exportToCSV } from '@/components/crm/csv-export';
import { CRM_BASE } from './types';
import type { NewQuoteDraft, PaymentSetupState, QuoteItemDraft } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterQuote as Quote,
  CrmMasterClientListRow as MasterClient,
  CrmMasterPriceList as PriceList,
  CrmMasterPriceListItem as PriceListItem,
  CrmMasterPipelineEntry as PipelineEntry,
  CrmMasterProduct as CrmProduct,
  CrmMasterQuotesDashboard as DashboardData,
  CrmMasterDuplicateWarning as DuplicateWarning,
  CrmMasterPaymentPlan as QuotePaymentPlan,
} from '@easyflow/shared';
import {
  isUserTypingInForm, isProductEligible, PIE_COLORS, getVatRate, AdvFilters, EMPTY_ADV_FILTERS, countAdvFilters, FilteredBadge, advFiltersToParams, AdvancedFilters
} from './shared';
import { CreateQuoteModal } from './preventivi/CreateQuoteModal';
import { EditQuoteModal } from './preventivi/EditQuoteModal';
import { PaymentSetupModal } from './preventivi/PaymentSetupModal';
import { DuplicateWarningDialog } from './preventivi/DuplicateWarningDialog';
import { QuotesTable } from './preventivi/QuotesTable';

// Task #3875 — lettura difensiva di campi legacy non presenti sul wire attuale.
const legacyStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

export function TabPreventivi({ onQuoteAccepted }: { onQuoteAccepted?: (entryId: string) => void }) {
  const { toast } = useToast();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [clients, setClients] = useState<MasterClient[]>([]);
  const [pipelineLeads, setPipelineLeads] = useState<PipelineEntry[]>([]);
  const [products, setProducts] = useState<CrmProduct[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [selectedPriceListId, setSelectedPriceListId] = useState<string>('');
  const [selectedPLItems, setSelectedPLItems] = useState<PriceListItem[]>([]);
  const [plItemsLoading, setPlItemsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientType, setClientType] = useState<'tenant' | 'lead' | 'client' | ''>('');
  const [newQuote, setNewQuote] = useState<NewQuoteDraft>({
    tenant_id: '', pipeline_entry_id: '', items: [], notes: '', payment_terms: '',
  });
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [advFilters, setAdvFilters] = useState<AdvFilters>(EMPTY_ADV_FILTERS);
  const [showArchived, setShowArchived] = useState(false);

  const [paymentSetupModal, setPaymentSetupModal] = useState<PaymentSetupState | null>(null);
  const [psInstallmentPlan, setPsInstallmentPlan] = useState<Array<{ number: number; amount: number; due_date: string; description: string }>>([]);
  const [psDuration, setPsDuration] = useState(12);
  const [psFrequency, setPsFrequency] = useState<'monthly' | 'quarterly' | 'semiannual' | 'annual'>('monthly');
  const [psStartDate, setPsStartDate] = useState(new Date().toISOString().split('T')[0] ?? '');
  const [psSending, setPsSending] = useState(false);
  const [psChargeFirstImmediately, setPsChargeFirstImmediately] = useState(false);
  const [psDateAdjustPending, setPsDateAdjustPending] = useState<{ index: number; oldDate: string; newDate: string; deltaDays: number } | null>(null);
  const [expandedQuotePlans, setExpandedQuotePlans] = useState<Record<string, QuotePaymentPlan[]>>({});
  const [loadingPlans, setLoadingPlans] = useState<Record<string, boolean>>({});

  const toggleQuotePlans = async (quoteId: string) => {
    if (expandedQuotePlans[quoteId]) {
      setExpandedQuotePlans(prev => { const next = { ...prev }; delete next[quoteId]; return next; });
      return;
    }
    setLoadingPlans(prev => ({ ...prev, [quoteId]: true }));
    try {
      const res = await api.get(`${CRM_BASE}/payment-plans/${quoteId}`);
      if (res.success && res.data) {
        setExpandedQuotePlans(prev => ({ ...prev, [quoteId]: Array.isArray(res.data) ? res.data : [] }));
      }
    } finally {
      setLoadingPlans(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' });
      if (statusFilter) params.set('status', statusFilter);
      if (showArchived) params.set('show_archived', 'true');
      const qs = params.toString();
      const dashQs = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' }).toString();
      const [quotesRes, dashRes] = await Promise.allSettled([
        api.get<Quote[] | { quotes?: Quote[] }>(`${CRM_BASE}/quotes${qs ? `?${qs}` : ''}`),
        api.get<DashboardData>(`${CRM_BASE}/quotes/dashboard${dashQs ? `?${dashQs}` : ''}`),
      ]);
      if (quotesRes.status === 'fulfilled' && quotesRes.value?.success && quotesRes.value.data) setQuotes(Array.isArray(quotesRes.value.data) ? quotesRes.value.data : (quotesRes.value.data).quotes || []);
      if (dashRes.status === 'fulfilled' && dashRes.value?.success && dashRes.value.data) setDashboard(dashRes.value.data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, advFilters, showArchived]);

  useEffect(() => { void loadQuotes(); }, [loadQuotes]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isUserTypingInForm()) return;
      void loadQuotes();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadQuotes]);

  const openCreate = async () => {
    try {
      const [cRes, pRes, plRes, leadsRes] = await Promise.all([
        api.get<MasterClient[] | { clients?: MasterClient[] }>(`${CRM_BASE}/clients`),
        api.get<CrmProduct[] | { products?: CrmProduct[] }>(`${CRM_BASE}/products`),
        api.get<PriceList[]>(`${CRM_BASE}/price-lists`),
        api.get<PipelineEntry[] | { entries?: PipelineEntry[] }>(`${CRM_BASE}/pipeline/entries`),
      ]);
      if (cRes.success && cRes.data) setClients(Array.isArray(cRes.data) ? cRes.data : (cRes.data).clients || []);
      if (pRes.success && pRes.data) setProducts(Array.isArray(pRes.data) ? pRes.data : (pRes.data).products || []);
      if (plRes.success && plRes.data) setPriceLists((Array.isArray(plRes.data) ? plRes.data : []).filter((pl) => pl.is_active));
      if (leadsRes.success && leadsRes.data) setPipelineLeads(Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data).entries || []);
      setNewQuote({ tenant_id: '', pipeline_entry_id: '', items: [], notes: '', payment_terms: '' });
      setClientSearch('');
      setClientType('');
      setSelectedPriceListId('');
      setSelectedPLItems([]);
      setProductSearch('');
      setShowCreate(true);
    } catch (e: unknown) {
      const __e = toErr(e);
      alert('Errore nel caricamento dei dati: ' + (__e.message || 'Errore sconosciuto'));
    }
  };

  const selectPriceList = async (plId: string) => {
    if (selectedPriceListId === plId) {
      setSelectedPriceListId('');
      setSelectedPLItems([]);
      return;
    }
    setSelectedPriceListId(plId);
    setPlItemsLoading(true);
    setProductSearch('');
    try {
      const res = await api.get(`${CRM_BASE}/price-lists/${plId}/items`);
      if (res.success && res.data) {
        const plItems = Array.isArray(res.data) ? res.data : [];
        const plItemMap = new Map(plItems.map((i) => [i.product_id, i]));
        // Mostriamo solo i prodotti effettivamente inclusi nel listino selezionato
        // (ed attivi), non l'intero catalogo. La merge con il dato prodotto serve
        // a recuperare codice/nome/categoria/IVA di fallback.
        const merged = products
          .filter((p) => isProductEligible(p) && plItemMap.has(p.id))
          .map((p) => {
            const plItem = plItemMap.get(p.id);
            return {
              product_id: p.id,
              product_code: p.code,
              product_name: p.name,
              base_price: p.net_price,
              price: plItem ? plItem.price : p.net_price,
              discount_percent: plItem ? plItem.discount_percent : 0,
              vat_rate: plItem?.vat_rate || p.vat_code || p.vat_rate || '22',
              product_vat_code: p.vat_code || p.vat_rate || '22',
              in_price_list: true,
              category: p.category,
              recurrence_type: p.recurrence_type,
            };
          });
        setSelectedPLItems(merged as unknown as PriceListItem[]);
      }
    } catch { setSelectedPLItems([]); }
    finally { setPlItemsLoading(false); }
  };

  const addItem = async (product: CrmProduct) => {
    let unitPrice = Number(product.net_price);
    let discountPercent = 0;
    let priceListName: string | null = null;

    if (newQuote.tenant_id) {
      try {
        const priceRes = await api.get<{ price?: number | string; discount_percent?: number | string; price_list_name?: string | null }>(`${CRM_BASE}/resolve-price/${product.id}?tenant_id=${newQuote.tenant_id}`);
        if (priceRes.success && priceRes.data) {
          const pd = priceRes.data;
          unitPrice = Number(pd.price);
          discountPercent = Number(pd.discount_percent) || 0;
          priceListName = pd.price_list_name || null;
        }
      } catch {}
    }

    setNewQuote((q) => ({
      ...q,
      items: [...q.items, {
        product_id: product.id, description: product.name + (priceListName ? ` [${priceListName}]` : ''), quantity: 1,
        unit_price: unitPrice, discount_percent: discountPercent, vat_rate: String(product.vat_code || product.vat_rate || '22'), vat_code: String(product.vat_code || product.vat_rate || '22'),
      }],
    }));
  };

  const updateItem = (idx: number, field: string, value: string | number) => {
    setNewQuote((q) => {
      const items = [...q.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...q, items };
    });
  };

  const removeItem = (idx: number) => {
    setNewQuote((q) => ({ ...q, items: q.items.filter((_, i: number) => i !== idx) }));
  };

  const calcTotals = () => {
    let net = 0, vat = 0;
    (newQuote.items || []).forEach((item) => {
      const lineNet = Number(item.quantity) * Number(item.unit_price) * (1 - Number(item.discount_percent || 0) / 100);
      const rate = getVatRate(String(item.vat_code || item.vat_rate || '22'));
      net += lineNet;
      vat += lineNet * (rate / 100);
    });
    return { net: net.toFixed(2), vat: vat.toFixed(2), gross: (net + vat).toFixed(2) };
  };

  const handleCreate = async () => {
    if (!newQuote.items || newQuote.items.length === 0) {
      alert('Aggiungi almeno un prodotto al preventivo prima di salvare.');
      return;
    }
    const pipelineEntryId = newQuote.pipeline_entry_id && newQuote.pipeline_entry_id.trim() !== '' ? newQuote.pipeline_entry_id : null;
    if (!pipelineEntryId) {
      alert('Seleziona un cliente o un lead prima di salvare il preventivo.');
      return;
    }
    setSaving(true);
    try {
      const totals = calcTotals();
      const payload = {
        ...newQuote,
        tenant_id: null,
        pipeline_entry_id: pipelineEntryId,
        subtotal_net: parseFloat(totals.net),
        total_vat: parseFloat(totals.vat),
        total_gross: parseFloat(totals.gross),
        issue_date: new Date().toISOString().split('T')[0] ?? '',
      };
      const res = await api.post(`${CRM_BASE}/quotes`, payload);
      if (res && !res.success) {
        alert('Errore nella creazione del preventivo: ' + (res.error || 'Errore sconosciuto'));
        return;
      }
      setShowCreate(false);
      void loadQuotes();
    } catch (e: unknown) {
      const __e = toErr(e);
      alert('Errore nella creazione del preventivo: ' + (__e.message || 'Errore sconosciuto'));
    } finally {
      setSaving(false);
    }
  };

  const [editQuoteModal, setEditQuoteModal] = useState<Quote | null>(null);
  const [editQuoteItems, setEditQuoteItems] = useState<QuoteItemDraft[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateWarning[] | null>(null);
  const [pendingLeadSelection, setPendingLeadSelection] = useState<PipelineEntry | null>(null);

  const psGeneratePlan = (total: number, duration: number, frequency: 'monthly' | 'quarterly' | 'semiannual' | 'annual', start: string) => {
    const freqMonths = frequency === 'monthly' ? 1 : frequency === 'quarterly' ? 3 : frequency === 'semiannual' ? 6 : 12;
    const count = Math.max(1, Math.floor(duration / freqMonths));
    const perInst = Math.round((total / count) * 100) / 100;
    const startD = new Date(start);
    const plan: Array<{ number: number; amount: number; due_date: string; description: string }> = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(startD);
      d.setMonth(d.getMonth() + freqMonths * (i + 1));
      plan.push({ number: i + 1, amount: i === count - 1 ? Math.round((total - perInst * (count - 1)) * 100) / 100 : perInst, due_date: d.toISOString().split('T')[0] ?? '', description: '' });
    }
    return plan;
  };

  const openPaymentSetupModal = (quote: Quote) => {
    const total = Number(quote.total_gross ?? 0);
    const start = new Date().toISOString().split('T')[0] ?? '';
    setPsDuration(12);
    setPsFrequency('monthly');
    setPsStartDate(start);
    setPsInstallmentPlan(psGeneratePlan(total, 12, 'monthly', start));
    setPsDateAdjustPending(null);
    setPsChargeFirstImmediately(false);
    setPaymentSetupModal(quote as unknown as PaymentSetupState);
  };

  const psOnDurationChange = (months: number) => {
    setPsDuration(months);
    if (paymentSetupModal) {
      const total = Number(paymentSetupModal.total_gross ?? 0);
      if (months === 0) {
        const start = psStartDate || (new Date().toISOString().split('T')[0] ?? '');
        setPsInstallmentPlan([{ number: 1, amount: total, due_date: start, description: '' }]);
        setPsFrequency('monthly');
      } else {
        setPsInstallmentPlan(psGeneratePlan(total, months, psFrequency, psStartDate));
      }
      setPsDateAdjustPending(null);
    }
  };

  const psOnFrequencyChange = (freq: 'monthly' | 'quarterly' | 'semiannual' | 'annual') => {
    setPsFrequency(freq);
    if (paymentSetupModal) {
      const total = Number(paymentSetupModal.total_gross ?? 0);
      setPsInstallmentPlan(psGeneratePlan(total, psDuration, freq, psStartDate));
      setPsDateAdjustPending(null);
    }
  };

  const psOnStartDateChange = (date: string) => {
    setPsStartDate(date);
    if (paymentSetupModal) {
      const total = Number(paymentSetupModal.total_gross ?? 0);
      setPsInstallmentPlan(psGeneratePlan(total, psDuration, psFrequency, date));
      setPsDateAdjustPending(null);
    }
  };

  const psUpdateInstallment = (index: number, field: string, value: string | number) => {
    const EMPTY_PS_INST = { number: 0, amount: 0, due_date: '', description: '' };
    if (field === 'due_date' && index < psInstallmentPlan.length - 1) {
      const oldDate = psInstallmentPlan[index]?.due_date;
      const newDate = value;
      if (oldDate && newDate && oldDate !== newDate) {
        const delta = Math.round((new Date(newDate).getTime() - new Date(oldDate).getTime()) / (1000 * 60 * 60 * 24));
        if (delta !== 0) {
          setPsInstallmentPlan(prev => {
            const updated = [...prev];
            updated[index] = { ...(updated[index] ?? EMPTY_PS_INST), due_date: String(newDate) };
            return updated;
          });
          setPsDateAdjustPending({ index, oldDate, newDate: String(newDate), deltaDays: delta });
          return;
        }
      }
    }
    setPsInstallmentPlan(prev => {
      const updated = [...prev];
      if (field === 'amount') {
        const newAmount = parseFloat(String(value)) || 0;
        updated[index] = { ...(updated[index] ?? EMPTY_PS_INST), amount: newAmount };
        const total = Number(paymentSetupModal?.total_gross ?? 0);
        const othersCount = updated.length - 1;
        if (othersCount > 0) {
          const remaining = Math.round((total - newAmount) * 100) / 100;
          const perOther = Math.round((remaining / othersCount) * 100) / 100;
          let distributed = 0;
          updated.forEach((_, i) => {
            if (i !== index) {
              if (i === updated.length - 1 || (i === updated.length - 2 && index === updated.length - 1)) {
                updated[i] = { ...(updated[i] ?? EMPTY_PS_INST), amount: Math.round((remaining - distributed) * 100) / 100 };
              } else {
                updated[i] = { ...(updated[i] ?? EMPTY_PS_INST), amount: perOther };
                distributed += perOther;
              }
            }
          });
        }
      } else {
        updated[index] = { ...(updated[index] ?? EMPTY_PS_INST), [field]: value };
      }
      return updated;
    });
  };

  const psApplyDateAdjustment = (adjustSubsequent: boolean) => {
    if (!psDateAdjustPending) return;
    const { index, deltaDays } = psDateAdjustPending;
    const EMPTY_PS_INST = { number: 0, amount: 0, due_date: '', description: '' };
    if (adjustSubsequent) {
      setPsInstallmentPlan(prev => {
        const updated = [...prev];
        for (let i = index + 1; i < updated.length; i++) {
          const cur = updated[i] ?? EMPTY_PS_INST;
          const d = new Date(cur.due_date);
          d.setDate(d.getDate() + deltaDays);
          updated[i] = { ...cur, due_date: d.toISOString().split('T')[0] ?? '' };
        }
        return updated;
      });
    }
    setPsDateAdjustPending(null);
  };

  const psAddInstallment = () => {
    setPsInstallmentPlan(prev => {
      const total = Number(paymentSetupModal?.total_gross ?? 0);
      const newCount = prev.length + 1;
      const perInst = Math.round((total / newCount) * 100) / 100;
      const lastDate = prev.length > 0 ? new Date(prev[prev.length - 1]?.due_date ?? psStartDate) : new Date(psStartDate);
      const freqMonths = psFrequency === 'monthly' ? 1 : psFrequency === 'quarterly' ? 3 : psFrequency === 'semiannual' ? 6 : 12;
      lastDate.setMonth(lastDate.getMonth() + freqMonths);
      const all = [...prev, { number: newCount, amount: 0, due_date: lastDate.toISOString().split('T')[0] ?? '', description: '' }];
      let distributed = 0;
      return all.map((inst, i) => {
        const amt = i === all.length - 1 ? Math.round((total - distributed) * 100) / 100 : perInst;
        if (i < all.length - 1) distributed += perInst;
        return { ...inst, number: i + 1, amount: amt };
      });
    });
  };

  const psRemoveInstallment = (index: number) => {
    setPsInstallmentPlan(prev => {
      const total = Number(paymentSetupModal?.total_gross ?? 0);
      const filtered = prev.filter((_, i) => i !== index);
      if (filtered.length === 0) return [];
      const perInst = Math.round((total / filtered.length) * 100) / 100;
      let distributed = 0;
      return filtered.map((inst, i) => {
        const amt = i === filtered.length - 1 ? Math.round((total - distributed) * 100) / 100 : perInst;
        if (i < filtered.length - 1) distributed += perInst;
        return { ...inst, number: i + 1, amount: amt };
      });
    });
  };

  const psPlanTotal = psInstallmentPlan.reduce((s, i) => s + (i.amount || 0), 0);
  const psQuoteTotal = Number(paymentSetupModal?.total_gross ?? 0);
  const psPlanValid = psInstallmentPlan.length > 0 && Math.abs(psPlanTotal - psQuoteTotal) < 0.02;

  const psEndDate = (() => {
    if (psDuration === 0) return psStartDate;
    const d = new Date(psStartDate);
    d.setMonth(d.getMonth() + psDuration);
    return d.toISOString().split('T')[0] ?? '';
  })();

  const handleSendPaymentSetup = async () => {
    if (!paymentSetupModal || !psPlanValid) return;
    setPsSending(true);
    try {
      const res = await api.post(`${CRM_BASE}/quotes/${paymentSetupModal.id}/send-payment-setup`, {
        installment_plan: psInstallmentPlan,
        duration_months: psDuration,
        frequency: psFrequency,
        start_date: psStartDate,
        end_date: psEndDate,
        total_amount: psQuoteTotal,
        charge_first_immediately: psChargeFirstImmediately,
      });
      if (!res.success) throw new Error(res.error || 'Errore invio setup pagamento');
      toast({ title: "Setup pagamento inviato", description: "L'email con il piano rateale e il link per configurare il metodo di pagamento è stata inviata al cliente." });
      const quoteId = paymentSetupModal.id as string;
      setPaymentSetupModal(null);
      setPsDateAdjustPending(null);
      setExpandedQuotePlans(prev => { const next = { ...prev }; delete next[quoteId]; return next; });
      void loadQuotes();
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: "Errore", description: __err.message || 'Errore invio', variant: "destructive" });
    } finally {
      setPsSending(false);
    }
  };

  const handleAction = async (id: string, action: string) => {
    try {
      if (action === 'send' || action === 'resend') {
        const res = await api.post(`${CRM_BASE}/quotes/${id}/send`);
        if (!res.success) throw new Error(res.error || 'Errore invio preventivo');
        toast({ title: "Preventivo inviato", description: "Il preventivo è stato inviato con successo via email. Il lead è stato spostato a 'Preventivo Inviato'." });
      }
      else if (action === 'duplicate') {
        const res = await api.post(`${CRM_BASE}/quotes/${id}/duplicate`);
        if (!res.success) throw new Error(res.error || 'Errore duplicazione');
        toast({ title: "Preventivo duplicato", description: "Il preventivo è stato duplicato con successo." });
      }
      else if (action === 'accept') {
        const res = await api.put<{ pipeline_entry_id?: string }>(`${CRM_BASE}/quotes/${id}/status`, { status: 'accepted' });
        if (!res.success) throw new Error(res.error || 'Errore accettazione');
        toast({ title: "Preventivo accettato", description: "Completa i dati per finalizzare il cliente." });
        const pipelineEntryId = (res.data)?.pipeline_entry_id;
        if (pipelineEntryId) {
          onQuoteAccepted?.(pipelineEntryId);
        } else {
          const quote = quotes.find(q => q.id === id);
          if (quote?.pipeline_entry_id && onQuoteAccepted) {
            onQuoteAccepted(quote.pipeline_entry_id);
          }
        }
      }
      else if (action === 'reject') {
        const res = await api.put(`${CRM_BASE}/quotes/${id}/status`, { status: 'rejected' });
        if (!res.success) throw new Error(res.error || 'Errore rifiuto');
        toast({ title: "Preventivo rifiutato", description: "Il preventivo è stato rifiutato. Il lead è stato spostato a 'Perso'." });
      }
      else if (action === 'revision') {
        const res = await api.put(`${CRM_BASE}/quotes/${id}/status`, { status: 'revision' });
        if (!res.success) throw new Error(res.error || 'Errore revisione');
        toast({ title: "Preventivo in revisione", description: "Il preventivo è stato messo in revisione. Il lead è stato spostato a 'Trattativa'." });
      }
      else if (action === 'pdf') {
        const token = localStorage.getItem('edgvoip_master_token') || localStorage.getItem('master_token') || localStorage.getItem('token');
        const response = await fetch(`/api${CRM_BASE}/quotes/${id}/pdf`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Errore scaricamento PDF');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Preventivo_${id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast({ title: "PDF scaricato", description: "Il PDF del preventivo è stato scaricato." });
        return;
      }
      else if (action === 'delete') {
        if (!confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
        const res = await api.delete(`${CRM_BASE}/quotes/${id}`);
        if (!res.success) throw new Error(res.error || 'Errore eliminazione');
        toast({ title: "Preventivo eliminato", description: "Il preventivo è stato eliminato." });
      }
      else if (action === 'archive') {
        const res = await api.post(`${CRM_BASE}/quotes/${id}/archive`);
        if (!res.success) throw new Error(res.error || 'Errore archiviazione');
        toast({ title: "Preventivo archiviato", description: "Il preventivo è stato archiviato." });
      }
      else if (action === 'restore') {
        const res = await api.post(`${CRM_BASE}/quotes/${id}/restore`);
        if (!res.success) throw new Error(res.error || 'Errore ripristino');
        toast({ title: "Preventivo ripristinato", description: "Il preventivo è stato ripristinato." });
      }
      else if (action === 'send-payment-setup') {
        const quoteRes = await api.get<Quote>(`${CRM_BASE}/quotes/${id}`);
        if (!quoteRes.success || !quoteRes.data) throw new Error('Impossibile caricare il preventivo');
        openPaymentSetupModal(quoteRes.data);
        return;
      }
      else if (action === 'edit') {
        const quoteRes = await api.get<Quote>(`${CRM_BASE}/quotes/${id}`);
        if (quoteRes.success && quoteRes.data) {
          const q = quoteRes.data;
          setEditQuoteModal(q);
          setEditQuoteItems((q.items || []).map((it) => ({
            product_id: it.product_id || '',
            description: it.description || '',
            quantity: it.quantity || 1,
            unit_price: it.unit_price || 0,
            discount_percent: it.discount_percent || 0,
            vat_rate: it.vat_rate || 22,
            vat_code: it.vat_code || '22',
          })));
        }
        return;
      }
      void loadQuotes();
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: "Errore", description: __e.message || 'Errore sconosciuto', variant: "destructive" });
    }
  };

  const handleSaveEditQuote = async () => {
    if (!editQuoteModal) return;
    setSavingEdit(true);
    try {
      const res = await api.put(`${CRM_BASE}/quotes/${editQuoteModal.id}`, { items: editQuoteItems });
      if (!res.success) throw new Error(res.error || 'Errore salvataggio');
      setEditQuoteModal(null);
      void loadQuotes();
    } catch (err: unknown) {
      const __err = toErr(err);
      alert(__err?.message || 'Errore nel salvataggio');
    } finally {
      setSavingEdit(false);
    }
  };

  const editCalcTotals = () => {
    let net = 0, vat = 0;
    editQuoteItems.forEach(item => {
      const qty = parseFloat(String(item.quantity)) || 1;
      const price = parseFloat(String(item.unit_price)) || 0;
      const discount = parseFloat(String(item.discount_percent)) || 0;
      const vatRate = parseFloat(String(item.vat_rate)) || 22;
      const lineNet = qty * price * (1 - discount / 100);
      const lineVat = lineNet * (vatRate / 100);
      net += lineNet;
      vat += lineVat;
    });
    return { net: net.toFixed(2), vat: vat.toFixed(2), gross: (net + vat).toFixed(2) };
  };

  const totals = calcTotals();
  // Task #3875: il wire /clients espone company_name/contact_name (mai `name`).
  const filteredClients = clients.filter(c => !clientSearch || (c.company_name || c.contact_name || '').toLowerCase().includes(clientSearch.toLowerCase()));
  const filteredLeads = pipelineLeads.filter(l => !clientSearch || (l.company_name || l.contact_name || '').toLowerCase().includes(clientSearch.toLowerCase()));
  const filteredPLProducts = selectedPLItems.filter(p => !productSearch || (p.product_name || legacyStr(p.name) || '').toLowerCase().includes(productSearch.toLowerCase()));
  const filteredAllProducts = products.filter(p => isProductEligible(p) && (!productSearch || p.name?.toLowerCase().includes(productSearch.toLowerCase())));

  const filteredQuotes = quotes.filter(q => {
    if (searchFilter && !(q.tenant_name || '').toLowerCase().includes(searchFilter.toLowerCase()) && !(q.quote_number || '').toLowerCase().includes(searchFilter.toLowerCase())) return false;
    return true;
  });

  const handleExportCSV = () => {
    exportToCSV(filteredQuotes as unknown as Record<string, unknown>[], 'preventivi', [
      { key: 'quote_number', label: 'Numero' },
      { key: 'tenant_name', label: 'Cliente' },
      { key: 'issue_date', label: 'Data' },
      { key: 'total_gross', label: 'Totale Lordo' },
      { key: 'status', label: 'Stato' },
    ]);
  };

  const statusPieData = dashboard?.by_status ? Object.entries(dashboard.by_status).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="space-y-4">
      {countAdvFilters(advFilters) > 0 && (
        <div className="flex justify-end"><FilteredBadge filters={advFilters} /></div>
      )}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Totale Preventivi', value: dashboard.total_quotes, icon: FileText, color: 'text-blue-400', fmt: 'num' },
            { label: 'Valore Totale', value: dashboard.total_value, icon: DollarSign, color: 'text-green-400', fmt: 'eur' },
            { label: 'Tasso Conversione', value: dashboard.conversion_rate, icon: TrendingUp, color: 'text-purple-400', fmt: 'pct' },
            { label: 'In Attesa', value: dashboard.pending_value, icon: Clock, color: 'text-amber-400', fmt: 'eur' },
          ].map((kpi, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">{kpi.label}</span>
                <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
              </div>
              <p className="text-2xl font-bold text-foreground dark:text-white">
                {kpi.fmt === 'eur' ? `€ ${Number(kpi.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : kpi.fmt === 'pct' ? `${kpi.value}%` : kpi.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {statusPieData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-foreground dark:text-white font-semibold mb-4">Distribuzione per Stato</h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width={300} height={200}>
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }: { name?: string; value?: number }) => `${name}: ${value}`}>
                  {statusPieData.map((_, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
            <input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Cerca preventivo..."
              className="pl-9 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm w-56" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
            <option value="">Tutti gli stati</option>
            <option value="draft">Bozza</option>
            <option value="sent">Inviato</option>
            <option value="revision">Da Rivedere</option>
            <option value="accepted">Accettato</option>
            <option value="payment_setup_sent">Setup Pagamento</option>
            <option value="payment_confirmed">Pagamento Confermato</option>
            <option value="rejected">Rifiutato</option>
            <option value="expired">Scaduto</option>
          </select>
          <AdvancedFilters filters={advFilters} onChange={setAdvFilters} dateLabel="Data emissione" />
          <label className="flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground/70 cursor-pointer select-none">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="rounded" />
            <Archive className="h-3.5 w-3.5" /> Mostra archiviati
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportCSV} className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white px-3 py-2 rounded-lg text-sm border border-border dark:border-gray-600">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus className="h-4 w-4" /> Nuovo Preventivo
          </button>
        </div>
      </div>

      <QuotesTable
        loading={loading} filteredQuotes={filteredQuotes}
        expandedQuotePlans={expandedQuotePlans} loadingPlans={loadingPlans}
        toggleQuotePlans={toggleQuotePlans} handleAction={handleAction}
      />

      <CreateQuoteModal
        showCreate={showCreate} setShowCreate={setShowCreate}
        clientSearch={clientSearch} setClientSearch={setClientSearch}
        clientType={clientType} setClientType={setClientType}
        newQuote={newQuote} setNewQuote={setNewQuote}
        filteredClients={filteredClients} filteredLeads={filteredLeads}
        setDuplicateWarning={setDuplicateWarning} setPendingLeadSelection={setPendingLeadSelection}
        selectedPriceListId={selectedPriceListId} setSelectedPriceListId={setSelectedPriceListId}
        setSelectedPLItems={setSelectedPLItems} selectPriceList={selectPriceList}
        priceLists={priceLists} productSearch={productSearch} setProductSearch={setProductSearch}
        plItemsLoading={plItemsLoading} filteredPLProducts={filteredPLProducts}
        addItem={addItem} updateItem={updateItem} removeItem={removeItem}
        totals={totals} saving={saving} handleCreate={handleCreate}
      />

      <EditQuoteModal
        editQuoteModal={editQuoteModal} setEditQuoteModal={setEditQuoteModal}
        editQuoteItems={editQuoteItems} setEditQuoteItems={setEditQuoteItems}
        editCalcTotals={editCalcTotals} savingEdit={savingEdit} handleSaveEditQuote={handleSaveEditQuote}
      />

      <PaymentSetupModal
        paymentSetupModal={paymentSetupModal} setPaymentSetupModal={setPaymentSetupModal}
        setPsDateAdjustPending={setPsDateAdjustPending}
        psQuoteTotal={psQuoteTotal} psDuration={psDuration} psOnDurationChange={psOnDurationChange}
        psInstallmentPlan={psInstallmentPlan} psStartDate={psStartDate} psOnStartDateChange={psOnStartDateChange}
        psEndDate={psEndDate} psFrequency={psFrequency} psOnFrequencyChange={psOnFrequencyChange}
        psAddInstallment={psAddInstallment} psUpdateInstallment={psUpdateInstallment} psRemoveInstallment={psRemoveInstallment}
        psDateAdjustPending={psDateAdjustPending} psApplyDateAdjustment={psApplyDateAdjustment}
        psPlanValid={psPlanValid} psPlanTotal={psPlanTotal}
        psChargeFirstImmediately={psChargeFirstImmediately} setPsChargeFirstImmediately={setPsChargeFirstImmediately}
        psSending={psSending} handleSendPaymentSetup={handleSendPaymentSetup}
      />

      <DuplicateWarningDialog
        duplicateWarning={duplicateWarning} setDuplicateWarning={setDuplicateWarning}
        pendingLeadSelection={pendingLeadSelection} setPendingLeadSelection={setPendingLeadSelection}
        setNewQuote={setNewQuote} setClientSearch={setClientSearch} setClientType={setClientType}
      />
    </div>
  );
}

